import { useEffect, useMemo } from 'react'
import { useAppContext } from '@/AppContext'
import { useRoomContext } from '@/RoomContext'
import { useUserContext } from '@/UserContext'
import { PinnedVideoInfo, sessionViewModeState, useVideoOrder } from '../../../shared/useViewMode'
import { VideoSource } from '../../../shared/Video2'
import * as Constants from '@/services/Constants'
import { MusePeer } from '@/services/RoomContextMusePeer'
import _ from 'lodash'

export function useVideoSources(pinnedVideo?: PinnedVideoInfo, page?: number): [VideoSource[], number] {
    const [roomState] = useRoomContext()
    const [appState] = useAppContext()
    const [userState] = useUserContext()
    const videoOrder = useVideoOrder()

    const peers = useMemo(
        () =>
            Object.keys(roomState.peers)
                .map((k) => roomState.peers[k])
                .filter((a) => a.connectionStatus !== 'initial')
                .filter((a) => !roomState.videoHiddenSessionIds.includes(a.sessionId))
                .filter((a) => a.clientType !== 'listenonly')
                .filter((a) => {
                    if (appState.appSettings.noShowEmptyCamerasOnGallery) {
                        return a.remoteVideo != null
                    }
                    return true
                })
                .filter((a) => {
                    if (pinnedVideo) {
                        return a.sessionId === pinnedVideo.peerSessionId
                    }
                    return true
                }),
        [
            appState.appSettings.noShowEmptyCamerasOnGallery,
            pinnedVideo,
            roomState.peers,
            roomState.videoHiddenSessionIds,
        ]
    )

    const showMyCamera = useMemo(() => {
        if (userState.clientType === 'listenonly') {
            return false
        }
        if (roomState.videoHiddenSessionIds.includes('_me')) {
            return false
        }
        if (userState.mediaStreams.video) {
            return true
        }
        // is empty video here; behavior is determined by the user setting.
        return !appState.appSettings.noShowEmptyCamerasOnGallery
    }, [
        appState.appSettings.noShowEmptyCamerasOnGallery,
        roomState.videoHiddenSessionIds,
        userState.clientType,
        userState.mediaStreams.video,
    ])

    const [vids, totalVids] = useMemo(() => {
        let vids: VideoSource[] = []

        const addPeer = (peer: MusePeer) => {
            if (pinnedVideo && pinnedVideo.peerSessionId !== peer.sessionId) {
                return
            }
            if (peer.remoteVideo && (!pinnedVideo || pinnedVideo.kind === 'camera')) {
                vids.push(new VideoSource(peer, 'camera', peer.remoteVideo))
            }
            if (peer.remoteDesktop && (!pinnedVideo || pinnedVideo.kind === 'screen')) {
                vids.push(new VideoSource(peer, 'screen', peer.remoteDesktop))
            }
            if (!peer.remoteVideo && (!pinnedVideo || pinnedVideo.kind === 'camera')) {
                vids.push(new VideoSource(peer, 'camera'))
            }
        }
        const addMe = (prepend?: boolean) => {
            if (!showMyCamera || pinnedVideo) {
                return
            }
            const myVid = new VideoSource(
                'me',
                'camera',
                userState.mediaStreams.video ?? undefined,
                userState.mediaStreams.videoProcessor?.id
            )
            myVid.mirrored = appState.appSettings.mirrorCamera
            if (prepend) {
                vids.unshift(myVid)
            } else {
                vids.push(myVid)
            }
        }

        // Build video list respeting video order
        for (const peerId of videoOrder) {
            if (peerId === userState.sessionId) {
                addMe()
            }
            const peer = peers.find((p) => p.sessionId === peerId)
            if (peer) {
                addPeer(peer)
            }
        }

        // Add any peers missing from the video order
        for (const peer of peers) {
            if (pinnedVideo && pinnedVideo.peerSessionId !== peer.sessionId) {
                continue
            }
            if (!videoOrder.includes(peer.sessionId)) {
                addPeer(peer)
            }
        }

        // Add self if missing from the video order
        if (!videoOrder.includes(userState.sessionId)) {
            addMe(true)
        }

        // Update video order state
        const newOrder = [...new Set(vids.map((vid) => vidToSessionId(vid, userState.sessionId)))]
        if (!_.isEqual(sessionViewModeState.mode.videoOrder, newOrder)) {
            sessionViewModeState.setVideoOrder(newOrder)
        }

        const totalVids = vids.length
        if (vids.length > Constants.max_videos_per_page) {
            const pg = page ?? 0
            vids = vids.slice(pg * Constants.max_videos_per_page, (pg + 1) * Constants.max_videos_per_page)
        }

        return [vids, totalVids]
    }, [
        videoOrder,
        peers,
        showMyCamera,
        userState.mediaStreams.video,
        userState.mediaStreams.videoProcessor?.id,
        pinnedVideo,
        appState.appSettings.mirrorCamera,
        userState.sessionId,
        page,
    ])

    return [vids, totalVids]
}

function vidToSessionId(vid: VideoSource, mySessionId: string) {
    if (vid.isMe) {
        return mySessionId
    }
    return vid.peer!.sessionId
}
